import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { Span } from '@repo/shared';

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { cn } from '@/lib/utils';
import { TraceContext } from '@/routes/(app)/(tracing)/context/trace-context';
import {
  formatDuration,
  formatOtelTimestamp,
  formatOtelTimestamp2,
} from '@/routes/(app)/(tracing)/lib/formatter';
import {
  allowedAiSpanAttributes,
  cleanString,
  transformKey,
} from '@/routes/(app)/(tracing)/lib/utils';

const AttributesValues = ({
  attributes,
  depth = 0,
  keyName,
}: {
  attributes: unknown;
  depth?: number;
  keyName?: string | Array<string>;
}) => {
  const { t } = useTranslation();
  // Handle all empty cases consistently
  if (
    attributes === null ||
    attributes === undefined ||
    (Array.isArray(attributes) && attributes.length === 0) ||
    (typeof attributes === 'object' && Object.keys(attributes).length === 0)
  ) {
    return (
      <span className='overflow-x-scroll text-sm'>
        {t('tracing.spanDetail.n/a')}
      </span>
    );
  }

  if (typeof attributes === 'string') {
    try {
      const attr = JSON.parse(attributes);

      if (typeof attr === 'object' || Array.isArray(attr)) {
        return <SyntaxHighlighter data={attr} />;
      }
    } catch {
      const val = attributes ? cleanString(attributes.toString()) : 'N/A';
      if (keyName === 'Input' && val === '[Not Serializable]') {
        return (
          <span className='overflow-x-scroll text-sm'>
            {t('tracing.spanDetail.noInput')}
          </span>
        );
      }
      return (
        <span className='overflow-x-scroll text-sm'>
          {attributes
            ? cleanString(attributes.toString())
            : t('tracing.spanDetail.n/a')}
        </span>
      );
    }
  }

  const processedValue = attributes;

  if (Array.isArray(processedValue)) {
    if (processedValue.length === 0) return null;
    return (
      <div className='relative'>
        <div className='mt-1 gap-3'>
          {processedValue.map((item, index) => (
            <div
              key={index}
              className='flex flex-col gap-1'>
              <AttributesValues
                key={index}
                attributes={item}
                depth={depth + 1}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (typeof processedValue === 'object') {
    const entries = Object.entries(processedValue);
    if (entries.length === 0) return null;

    return (
      <div className='relative'>
        <div className='mt-1'>
          {entries.map(([key, val]) => (
            <div
              key={key}
              className='flex flex-col gap-2 p-2 pl-0'>
              <span className='text-mastra-el-3 text-sm capitalize'>
                {transformKey(key)}
              </span>
              <AttributesValues
                attributes={val}
                depth={depth + 1}
                keyName={transformKey(key)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (typeof processedValue === 'boolean')
    return (
      <span className='font-mono text-sm'>
        {processedValue.toString() || t('tracing.spanDetail.n/a')}
      </span>
    );
  if (typeof processedValue === 'number')
    return (
      <span className='font-mono text-sm'>{processedValue.toString()}</span>
    );
  if (typeof processedValue === 'string')
    return (
      <span className='font-mono text-sm'>
        {processedValue
          ? cleanString(processedValue.toString())
          : t('tracing.spanDetail.n/a')}
      </span>
    );

  return <span className='text-gray-400'>{String(processedValue)}</span>;
};

const Events = ({ span }: { span: Span }) => {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col px-2'>
      <p className='text-lg'>{t('tracing.spanDetail.events')}</p>
      {span.events.map((event) => {
        const eventAttributes = event.attributes.map((att) => ({
          [att.key]: att.value,
        }));
        return (
          <div
            key={event.name}
            className={cn(
              'flex flex-col gap-2 border-b-[0.5px] pb-2 pt-4 first:pb-4 last:border-b-0'
            )}>
            <p className='text-mastra-el-3 text-xs'>
              {t('tracing.spanDetail.name')}
            </p>
            <p className='font-mono text-xs'>{event.name}</p>
            <p className='text-mastra-el-3 text-xs'>
              {t('tracing.spanDetail.time')}
            </p>
            <p className='font-mono text-xs'>
              {event.timeUnixNano
                ? formatOtelTimestamp2(Number(event.timeUnixNano))
                : ''}
            </p>
            {event.attributes.length > 0 ? (
              <AttributesValues attributes={eventAttributes} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const Attributes = ({ span }: { span: Span }) => (
  <AttributesValues attributes={span.attributes} />
);

export const SpanDetail = () => {
  const { span } = useContext(TraceContext);
  const { t } = useTranslation();
  const isAiSpan = span?.name.startsWith('ai.');

  const aiSpanAttributes = Object.entries(span?.attributes ?? {})
    .filter(([key]) => allowedAiSpanAttributes.includes(key))
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value,
      }),
      {}
    );

  return (
    <ScrollArea className='m-2 h-full'>
      <h2 className='text-2xl font-semibold'>{span?.name}</h2>
      <div className='grid grid-cols-3 gap-1 px-6'>
        <div className='flex flex-col gap-1'>
          <span className='text-mastra-el-3 text-xs'>
            {t('tracing.spanDetail.duration')}
          </span>
          <span className='font-mono text-xs'>
            {span?.duration ? formatDuration(span.duration) : ''}ms
          </span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-mastra-el-3 text-xs'>
            {t('tracing.spanDetail.time')}
          </span>
          <span className='font-mono text-xs'>
            {span?.startTime ? formatOtelTimestamp(span.startTime) : ''}
          </span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-mastra-el-3 text-xs'>
            {t('tracing.spanDetail.status')}
          </span>
          {span?.status.code == 0 ? (
            <Badge variant={'success'}>{t('tracing.spanDetail.ok')}</Badge>
          ) : (
            <Badge variant={'destructive'}>
              {t('tracing.spanDetail.error')}
            </Badge>
          )}
        </div>
      </div>
      {span?.status.code !== 0 ? (
        <div className='border-t-[0.5px] px-6 pt-4'>
          {span && span.events.length > 0 && <Events span={span} />}
        </div>
      ) : null}
      <div className='border-t-[0.5px] px-6 pt-4'>
        {span && (
          <Attributes
            span={{
              ...span,
              attributes: isAiSpan ? aiSpanAttributes : span.attributes,
            }}
          />
        )}
      </div>
      {span?.status.code === 0 ? (
        <div className='border-t-[0.5px] px-6 pt-4'>
          {span.events.length > 0 && <Events span={span} />}
        </div>
      ) : null}
    </ScrollArea>
  );
};
